class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle?.querySelector("summary").nextElementSibling;

    this.mainDetailsToggle?.addEventListener(
      "focusout",
      this.onFocusOut.bind(this)
    );
    this.mainDetailsToggle?.addEventListener(
      "toggle",
      this.onToggle.bind(this)
    );
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;
      const megaMenuPanel = document.getElementById("mega-menu-panel");
      if (megaMenuPanel && megaMenuPanel.contains(document.activeElement))
        return;
      this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

// Desktop hover for the website mega menus. The open menu's content is moved
// into #mega-menu-panel under the bar (sections/header.liquid), so the hover
// region is the labels row plus that panel:
// - Switching to another label waits SWITCH_DELAY while a menu is open, and is
//   dropped when the pointer moves on: the dropdown links sit far right of the
//   labels, so the diagonal path down to them crosses the neighbouring labels,
//   which used to swap the panel mid-way.
// - Closing waits CLOSE_DELAY and only starts once the pointer is outside the
//   labels row and the panel (logo, search, switcher tabs, page), so a slow
//   move through the bar between a label and the panel keeps it open.
const HEADER_MENU_SWITCH_DELAY = 180;
const HEADER_MENU_CLOSE_DELAY = 300;

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
    this.switchTimeout = null;
    if (this.mainDetailsToggle) {
      this.mainDetailsToggle.addEventListener(
        "mouseenter",
        this.onMouseEnter.bind(this)
      );
      this.mainDetailsToggle.addEventListener(
        "mouseleave",
        this.onMouseLeave.bind(this)
      );
    }
    HeaderMenu.setupRegionListener();
  }

  static isDesktop() {
    return window.matchMedia("(min-width: 1000px)").matches;
  }

  static openMenus() {
    return Array.from(document.querySelectorAll("header-menu")).filter(
      (menu) => menu.mainDetailsToggle?.hasAttribute("open")
    );
  }

  static cancelClose() {
    clearTimeout(HeaderMenu.closeTimeout);
    HeaderMenu.closeTimeout = null;
  }

  static scheduleClose() {
    if (HeaderMenu.closeTimeout) return;
    HeaderMenu.closeTimeout = setTimeout(() => {
      HeaderMenu.closeTimeout = null;
      HeaderMenu.openMenus().forEach((menu) => menu.close());
    }, HEADER_MENU_CLOSE_DELAY);
  }

  // One listener for all menus: every pointer move over the header decides
  // whether the open menu stays (labels row or panel) or starts closing.
  static setupRegionListener() {
    const wrapper = document.querySelector(".header-wrapper");
    if (!wrapper || wrapper.hasAttribute("data-header-menu-listener")) return;
    wrapper.setAttribute("data-header-menu-listener", "true");

    wrapper.addEventListener("mouseover", (event) => {
      if (!HeaderMenu.isDesktop()) return;
      const inRegion = event.target.closest(
        ".header__inline-menu, #mega-menu-panel"
      );
      if (inRegion) HeaderMenu.cancelClose();
      else if (HeaderMenu.openMenus().length) HeaderMenu.scheduleClose();
    });
    wrapper.addEventListener("mouseleave", () => {
      if (!HeaderMenu.isDesktop()) return;
      if (HeaderMenu.openMenus().length) HeaderMenu.scheduleClose();
    });
  }

  onMouseEnter() {
    if (!HeaderMenu.isDesktop()) return;
    HeaderMenu.cancelClose();
    clearTimeout(this.switchTimeout);
    if (this.mainDetailsToggle.hasAttribute("open")) return;
    if (HeaderMenu.openMenus().length) {
      this.switchTimeout = setTimeout(() => this.open(), HEADER_MENU_SWITCH_DELAY);
    } else {
      this.open();
    }
  }

  onMouseLeave() {
    clearTimeout(this.switchTimeout);
    this.switchTimeout = null;
  }

  open() {
    this.switchTimeout = null;
    document.querySelectorAll("details-modal").forEach((modal) => {
      if (modal.isOpen()) modal.close(false);
    });

    this.mainDetailsToggle.setAttribute("open", "");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", "true");
    // Then close all other HeaderMenu instances
    // Since this menu is now open, the close handlers will skip content removal
    HeaderMenu.openMenus().forEach((menu) => {
      if (menu !== this) menu.close();
    });
  }

  close() {
    clearTimeout(this.switchTimeout);
    this.switchTimeout = null;
    super.close();
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (
      document.documentElement.style.getPropertyValue(
        "--header-bottom-position-desktop"
      ) !== ""
    )
      return;
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define("header-menu", HeaderMenu);
